// 변경공사 상태 전이 — `0026` 의 change_order_rpc_only 전이쌍과 같다(migrations 테스트가 맞춰 본다).

import {
  CHANGE_ORDER_ALREADY_SENT_MESSAGE,
  CHANGE_ORDER_CONTRACT_NOT_SIGNED_MESSAGE,
  CHANGE_ORDER_EMPTY_MESSAGE,
  CHANGE_ORDER_NO_LINK_MESSAGE,
  CHANGE_ORDER_PROJECT_INACTIVE_MESSAGE,
  CHANGE_ORDER_WARRANTY_EXTEND_MESSAGE,
  CHANGE_ORDER_WARRANTY_EXTEND_SENT_NOTICE,
} from "./messages";
import { CHANGE_ORDER_STATUS_LABEL, type ChangeOrderStatus } from "./types";
import type { ContractStatus } from "../contract/types";
import type { ProjectStatus } from "../project/types";

export const CHANGE_ORDER_TRANSITIONS: Record<ChangeOrderStatus, readonly ChangeOrderStatus[]> = {
  DRAFT: ["SENT", "ACCEPTED", "VOID"],
  SENT: ["ACCEPTED", "REJECTED", "VOID"],
  ACCEPTED: [],
  REJECTED: [],
  VOID: [],
};

export const canTransition = (from: ChangeOrderStatus, to: ChangeOrderStatus): boolean =>
  CHANGE_ORDER_TRANSITIONS[from].includes(to);

// 사용자에게 나가는 문구라 상태는 한글 라벨로 적는다.
export const changeOrderTransitionMessage = (from: ChangeOrderStatus, to: ChangeOrderStatus): string =>
  `변경공사를 「${CHANGE_ORDER_STATUS_LABEL[from]}」에서 「${CHANGE_ORDER_STATUS_LABEL[to]}」 상태로 바꿀 수 없습니다`;

export const assertTransition = (from: ChangeOrderStatus, to: ChangeOrderStatus): void => {
  if (!canTransition(from, to)) {
    throw new Error(changeOrderTransitionMessage(from, to));
  }
};

export const isClosed = (status: ChangeOrderStatus): boolean =>
  CHANGE_ORDER_TRANSITIONS[status].length === 0;

export const canEditDraft = (status: ChangeOrderStatus): boolean => status === "DRAFT";

export interface ChangeOrderGate {
  status: ChangeOrderStatus;
  contract_status: ContractStatus;
  project_status: ProjectStatus;
  // 이 계약에 이 건 말고 SENT 가 있는가.
  other_sent: boolean;
  item_count: number;
  extend_days: number | null;
}

// 변경공사를 할 수 있는 현장 — 유지보수 중 추가 요청까지(완료 현장은 막는다 · `0067` RPC 와 같은 목록).
export const CHANGE_ORDER_ACTIVE_PROJECT: readonly ProjectStatus[] = ["SCHEDULED", "IN_PROGRESS", "WARRANTY"];

// 공기 연장이 일정을 옮기는 현장 — 준공한 현장(유지보수)은 옮길 준공 예정일이 없다.
export const CHANGE_ORDER_SCHEDULE_PROJECT: readonly ProjectStatus[] = ["SCHEDULED", "IN_PROGRESS"];

// 공기 증감을 받는 현장인가 — 변경공사는 되지만 일정은 못 옮기는 유지보수 현장만 아니다.
export const extendAllowedFor = (status: ProjectStatus | null | undefined): boolean =>
  !status || !CHANGE_ORDER_ACTIVE_PROJECT.includes(status) || CHANGE_ORDER_SCHEDULE_PROJECT.includes(status);

const commonBlock = (g: ChangeOrderGate): string | null => {
  if (g.contract_status !== "SIGNED") return CHANGE_ORDER_CONTRACT_NOT_SIGNED_MESSAGE;
  if (!CHANGE_ORDER_ACTIVE_PROJECT.includes(g.project_status)) return CHANGE_ORDER_PROJECT_INACTIVE_MESSAGE;
  return null;
};

const draftBlock = (g: ChangeOrderGate): string | null => {
  if (g.other_sent) return CHANGE_ORDER_ALREADY_SENT_MESSAGE;
  if (g.item_count === 0 && (g.extend_days ?? 0) === 0) return CHANGE_ORDER_EMPTY_MESSAGE;
  return null;
};

// 전이·계약·현장까지만 — 인메모리는 이 뒤에 유지보수 공기 증감을 타입 오류로 올린다(`0067` SQL 과 같은 순서).
export const gateBaseBlock = (g: ChangeOrderGate, to: "SENT" | "ACCEPTED"): string | null =>
  canTransition(g.status, to) ? commonBlock(g) : changeOrderTransitionMessage(g.status, to);

// 유지보수 현장에 공기 증감이 실렸는가 — 작성 중이면 비우고 저장, 보낸 건은 파기 후 다시.
export const warrantyExtendBlock = (g: ChangeOrderGate): string | null => {
  if (!g.extend_days || extendAllowedFor(g.project_status)) return null;
  return g.status === "DRAFT" ? CHANGE_ORDER_WARRANTY_EXTEND_MESSAGE : CHANGE_ORDER_WARRANTY_EXTEND_SENT_NOTICE;
};

// 보내기를 막는 이유(없으면 null) — ★3 금액 대조는 RPC 가 한다.
export const sendBlockReason = (g: ChangeOrderGate & { live_link: boolean }): string | null =>
  gateBaseBlock(g, "SENT") ?? warrantyExtendBlock(g) ?? (g.live_link ? null : CHANGE_ORDER_NO_LINK_MESSAGE) ?? draftBlock(g);

// 승인을 막는 이유(없으면 null) — DRAFT 는 종이 승인이라 링크를 보지 않는다.
export const acceptBlockReason = (g: ChangeOrderGate): string | null =>
  gateBaseBlock(g, "ACCEPTED") ?? warrantyExtendBlock(g) ?? (g.status === "DRAFT" ? draftBlock(g) : null);
